import { AppState } from 'src/AppState/AppState';
import { MarketCashOutPropsType } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/marketCashOut/MarketCashOut';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { ChangeEvent } from 'react';
import {
    moneyForPayload,
    moneyForView,
} from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/marketLimits/MarketLimits.state';

function specialNumericValidator(value: string): string | null {
    const numericValue = parseFloat(value);
    if ((value.trim() !== '' && isNaN(numericValue)) || numericValue < 0 || numericValue > 1) {
        return 'Value must be between [0-1]';
    }
    return null;
}

function numericValidator(value: string): string | null {
    const numericValue = parseFloat(value);
    if ((value.trim() !== '' && isNaN(numericValue)) || numericValue < 0 || numericValue > 20) {
        return 'Value must be between [0-20]';
    }
    return null;
}

export class MarketCashOutState {
    @observable baseCashoutValue: string | null = null;
    @observable baseCashoutError: string | null = null;

    @observable cashoutOverroundValue: string | null = null;
    @observable cashoutOverroundError: string | null = null;

    @observable cashoutOfferEasingValue: string | null = null;
    @observable cashoutOfferEasingError: string | null = null;

    constructor(
        readonly appState: AppState,
        readonly props: MarketCashOutPropsType
    ) {
        makeAutoObservable(this);
    }

    @computed get baseCashout(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        const baseCashout =
            typeof marketModel.cashoutMarketSettings?.baseCashout === 'number'
                ? moneyForView(marketModel.cashoutMarketSettings?.baseCashout)
                : '';

        return this.baseCashoutValue !== null ? this.baseCashoutValue : baseCashout.toString();
    }

    @action onChangeBaseCashout = (event: ChangeEvent<HTMLInputElement>): void => {
        let value = event.target.value;
        if (value.includes(',')) {
            value = value.replace(',', '.');
        }

        this.baseCashoutError = specialNumericValidator(value);
        this.baseCashoutValue = value;
    };

    @action onSaveBaseCashout = (): void => {
        if (this.baseCashoutValue !== null && this.baseCashoutError === null) {
            const payload = {
                cashoutMarketSettings: {
                    baseCashout: moneyForPayload(this.baseCashoutValue.trim() === '' ? '0' : this.baseCashoutValue),
                },
            };
            this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
        }
    };

    @computed get cashoutOverround(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        const cashoutOverround =
            typeof marketModel.cashoutMarketSettings?.cashoutOverround === 'number'
                ? moneyForView(marketModel.cashoutMarketSettings.cashoutOverround)
                : '';

        return this.cashoutOverroundValue !== null ? this.cashoutOverroundValue : cashoutOverround.toString();
    }

    @action onChangeCashoutOverround = (event: ChangeEvent<HTMLInputElement>): void => {
        let value = event.target.value;
        if (value.includes(',')) {
            value = value.replace(',', '.');
        }

        this.cashoutOverroundError = specialNumericValidator(value);
        this.cashoutOverroundValue = value;
    };

    @action onSaveCashoutOverround = (): void => {
        if (this.cashoutOverroundValue !== null && this.cashoutOverroundError === null) {
            const payload = {
                cashoutMarketSettings: {
                    cashoutOverround: moneyForPayload(
                        this.cashoutOverroundValue.trim() === '' ? '0' : this.cashoutOverroundValue
                    ),
                },
            };
            this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
        }
    };

    @computed get cashoutOfferEasing(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        const cashoutOfferEasing =
            typeof marketModel.cashoutMarketSettings?.cashoutOfferEasing === 'number'
                ? moneyForView(marketModel.cashoutMarketSettings.cashoutOfferEasing)
                : '';

        return this.cashoutOfferEasingValue !== null ? this.cashoutOfferEasingValue : cashoutOfferEasing.toString();
    }

    @action onChangeCashoutOfferEasing = (event: ChangeEvent<HTMLInputElement>): void => {
        let value = event.target.value;
        if (value.includes(',')) {
            value = value.replace(',', '.');
        }

        this.cashoutOfferEasingError = numericValidator(value);
        this.cashoutOfferEasingValue = value;
    };

    @action onSaveCashoutOfferEasing = (): void => {
        if (this.cashoutOfferEasingValue !== null && this.cashoutOfferEasingError === null) {
            const payload = {
                cashoutMarketSettings: {
                    cashoutOfferEasing: moneyForPayload(
                        this.cashoutOfferEasingValue.trim() === '' ? '0' : this.cashoutOfferEasingValue
                    ),
                },
            };
            this.appState.eventStateActions.saveMarketChanges(this.props.marketId, payload);
        }
    };

    @action onChangeCashoutAvailable = (event: ChangeEvent<HTMLInputElement>): void => {
        this.appState.eventStateActions.saveMarketChanges(this.props.marketId, {
            cashoutAvailable: event.target.checked,
        });
    };
}
